<template>
  <div>
    <!-- Individual segment cards -->
    <v-card
      v-for="(segment, idx) in analysis.segments"
      :key="idx"
      variant="outlined"
      class="mt-3"
    >
      <v-card-text>
        <!-- Header: check type + status chip -->
        <div class="d-flex align-center justify-space-between">
          <span class="text-subtitle-2 font-weight-bold">
            {{ $t('routes.pod.authenticity.modelGenai') }}
          </span>
          <v-chip
            :color="segmentStatusColor(segment.status)"
            variant="flat"
            size="small"
          >
            {{ $t(`routes.pod.authenticity.status.${segment.status}`) }}
          </v-chip>
        </div>

        <v-divider class="my-3" />

        <!-- Authenticity % -->
        <div class="detail-row">
          <span class="text-medium-emphasis">
            {{ $t('routes.pod.authenticity.scorecard.authenticity') }}
          </span>
          <span
            class="font-weight-bold"
            :style="{ color: segmentAuthenticityColor(segment.overallscore) }"
          >
            {{ segment.overallscore != null ? (100 - segment.overallscore) + '%' : '-' }}
          </span>
        </div>

        <!-- Segment range -->
        <div class="detail-row">
          <span class="text-medium-emphasis">
            {{ $t('routes.pod.authenticity.segment') }}
          </span>
          <span>
            {{ formatDuration(segment.startsecond) }} - {{ formatDuration(segment.endsecond) }}
          </span>
        </div>

        <!-- Forensic frames (expandable) -->
        <v-expansion-panels
          v-if="segment.forensicFrames && segment.forensicFrames.length > 0"
          variant="accordion"
          class="mt-3"
        >
          <v-expansion-panel>
            <v-expansion-panel-title class="text-subtitle-2">
              {{ $t('routes.pod.authenticity.forensicDetails') }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-table density="compact">
                <thead>
                  <tr>
                    <th>{{ $t('routes.pod.authenticity.timestamp') }}</th>
                    <th class="text-end">{{ $t('routes.pod.authenticity.aiLikelihood') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(frame, fIdx) in segment.forensicFrames" :key="fIdx">
                    <td>{{ frame.timestamp != null ? formatTimestamp(frame.timestamp) : '-' }}</td>
                    <td class="text-end" :class="{ 'text-error': frameScore(frame) > 0.7 }">
                      {{ (frameScore(frame) * 100).toFixed(0) }}%
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Credits + date -->
        <div
          v-if="segment.creditsused || segment.completedat"
          class="d-flex justify-space-between text-body-2 text-medium-emphasis mt-3"
        >
          <span v-if="segment.creditsused">
            {{ $t('routes.pod.authenticity.creditsUsed') }}: {{ segment.creditsused }}
          </span>
          <span v-else />
          <span v-if="segment.completedat">{{ formatDate(segment.completedat) }}</span>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'AuthenticitySegmentScorecard',
  props: {
    analysis: {
      type: Object,
      required: true,
    },
  },
  methods: {
    segmentStatusColor(status) {
      switch (status) {
        case 'AUTHENTIC': return 'success';
        case 'SUSPICIOUS': return 'warning';
        case 'AI_GENERATED': return 'error';
        default: return 'grey';
      }
    },
    segmentAuthenticityColor(overallscore) {
      if (overallscore == null) return undefined;
      const pct = 100 - overallscore;
      if (pct >= 70) return 'rgb(var(--v-theme-success))';
      if (pct >= 30) return 'rgb(var(--v-theme-warning))';
      return 'rgb(var(--v-theme-error))';
    },
    formatTimestamp(ms) {
      const seconds = ms / 1000;
      if (seconds < 60) {
        return seconds.toFixed(1) + 's';
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    },
    formatDuration(seconds) {
      if (seconds == null) return '-';
      if (seconds < 60) return `${seconds}s`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    },
    frameScore(frame) {
      return frame.aiGenerated ?? 0;
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}
</style>
